// ---------------------------------------------------------------------------
// simulation.ts — pure, client-side position-sizing simulation for the
// Conviction Screener. No API calls, no React. Operates on already-fetched
// rows mapped to `SimMarket`.
//
// What it does
//   Sizes hypothetical positions by a chosen signal (dominant capital, market
//   volume, capital/volume ratio, or a blend), applies liquidity + per-event
//   caps, and scores snapshot mark-to-market PnL. It evaluates *sizing*, not
//   entry/exit *timing* (see the plan's §0 "snapshot, not a backtest").
//
// The one correctness item — dollar-denominated PnL (plan §0):
//   stake is in DOLLARS. A dollar buys fewer shares at a higher entry price.
//     shares_i = stake$_i / a_i
//     pnl_i    = stake$_i * (m_i / a_i - 1)        // == shares_i * (m_i - a_i)
//   where a_i = avgEntry (dominant side), m_i = markPrice (live, dominant side).
//   Markets with a_i == null or <= 0 are un-simulatable -> skipped (and counted).
//
// No Kelly: in snapshot mode the live price has drifted from the group's entry,
// so the Kelly edge is stale. We size on signals stable at the snapshot.
// ---------------------------------------------------------------------------

export type DominantSide = 'YES' | 'NO';

// ---------------------------------------------------------------------------
// Inputs
// ---------------------------------------------------------------------------

/**
 * A single market, reduced to exactly what sizing + PnL need. The caller
 * (Screener.tsx) builds this from a ConvictionMarket via computeRowMetrics:
 *   avgEntry        <- computeRowMetrics(m).avgEntry
 *   markPrice       <- computeRowMetrics(m).outcomeMarketProb
 *   dominantCapital <- max(capital_usd_yes, capital_usd_no)
 *   volume          <- m.volume
 */
export interface SimMarket {
    marketId: string;
    eventId: string | null;
    dominantSide: DominantSide | null;
    avgEntry: number | null; // a — avg entry price the group paid (dominant side)
    markPrice: number | null; // m — live outcome price (dominant side)
    dominantCapital: number; // max(capital_usd_yes, capital_usd_no)
    volume: number | null; // total traded volume (coarse depth proxy)
}

/** Which stable snapshot signal drives proportional weights. */
export type WeightSignal = 'capital' | 'volume' | 'capitalToVolume' | 'blend';

/**
 * Deployment total. Resolved to T (dollars to deploy across the book) by
 * `resolveDeploymentTotal`. "both" from the open questions: a fraction of
 * bankroll, OR an absolute figure.
 */
export type Deployment =
    | { mode: 'fraction'; deployFraction: number } // T = deployFraction * bankroll
    | { mode: 'absolute'; totalCapital: number }; // T = totalCapital

/**
 * Sizing algorithms.
 *  - fixedDollars / fixedShares: per-market constant; book total is emergent.
 *  - fixedFraction: equal split of the deployment total T.
 *  - volumeFraction: stake$_i = f * volume_i. The "bet by available liquidity"
 *    lens — total is emergent and may exceed bankroll (utilization can be >1).
 *  - proportional: the headline algo; weights by `signal`, concentrated by α,
 *    scaled to the deployment total T.
 */
export type SizingAlgo =
    | { kind: 'fixedDollars'; dollarsPerMarket: number }
    | { kind: 'fixedShares'; sharesPerMarket: number }
    | { kind: 'fixedFraction' }
    | { kind: 'volumeFraction'; volumeFraction: number }
    | {
        kind: 'proportional';
        signal: WeightSignal;
        blendBeta: number; // β, only for signal==='blend': w = cap^β * vol^(1-β)
        concentration: number; // α exponent on the weight
    };

export interface SimWrappers {
    /** Cap stake at k * volume_i. null = off. */
    liquidityCapFrac: number | null;
    /** Cap total stake per event at this * bankroll. null = off. */
    maxStakePerEventFrac: number | null;
    /**
     * Re-spread dollars freed by a binding cap onto still-uncapped markets,
     * keeping the book at its target total. Default OFF: leaving the book
     * under-deployed and showing utilization < 1 IS the liquidity signal.
     * Only meaningful for budgeted algos (fixedFraction, proportional).
     */
    redistributeCapped: boolean;
}

export interface SimFees {
    /** Flat cost charged once per placed bet. Default 0. */
    perTrade: number;
    /** Proportional cost in basis points of stake. Default 0. */
    bps: number;
}

export interface SimConfig {
    bankroll: number;
    deployment: Deployment;
    algo: SizingAlgo;
    wrappers: SimWrappers;
    fees: SimFees;
    /**
     * Market-selection rule (NOT a dollar cap — that's maxStakePerEventFrac).
     *  false → trade every market that passes the filters (siblings included).
     *  true  → trade at most one market per event: the first in input order, which
     *          is the API's ranking, so the top-ranked sibling per event is kept and
     *          the rest are excluded from the book entirely.
     */
    oneBetPerEvent: boolean;
    /** Exclude markets where dominantCapital < this value. null = off. */
    minCapitalThreshold: number | null;
    /** Exclude markets where volume < this value. null = off. */
    minVolumeThreshold: number | null;
}

// ---------------------------------------------------------------------------
// Outputs
// ---------------------------------------------------------------------------

export interface BetResult {
    marketId: string;
    eventId: string | null;
    side: DominantSide | null;
    entryPrice: number | null; // a
    markPrice: number | null; // m
    capitalToVolume: number | null; // dominantCapital / volume (the 5%-vs-0.1% number)
    stakeDollars: number;
    stakeShares: number;
    grossPnl: number; // before fees
    fee: number;
    pnl: number; // net of fees
    ret: number; // net pnl / stakeDollars (0 if no stake)
    capped: boolean; // a cap bound this stake down
    skipped: boolean; // un-simulatable (a == null / <= 0)
    filteredByThreshold: boolean;
}

export interface SimSummary {
    bankroll: number;
    deploymentTotal: number; // T, the intended budget (0 for emergent-total algos)
    totalStaked: number;
    totalGrossPnl: number;
    totalFees: number;
    totalPnl: number; // net
    roi: number | null; // totalPnl / totalStaked
    hitRate: number | null; // #(pnl>0) / nBets
    meanRet: number | null;
    medianRet: number | null;
    worstRet: number | null; // min ret across bets
    nBets: number; // placed (stake > 0, not skipped)
    nCapped: number;
    nSkipped: number;
    nExcludedByEvent: number; // siblings dropped by oneBetPerEvent
    utilization: number; // totalStaked / bankroll (can exceed 1)
    nFilteredByThreshold: number; // excluded by min capital or volume threshold
}

export interface SimResult {
    bets: BetResult[];
    summary: SimSummary;
}

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

function isSimulatable(m: SimMarket): boolean {
    return (
        m.avgEntry != null &&
        m.avgEntry > 0 &&
        m.markPrice != null &&
        Number.isFinite(m.avgEntry) &&
        Number.isFinite(m.markPrice)
    );
}

/** dominantCapital / volume, or null when volume is missing / non-positive. */
export function capitalToVolume(m: SimMarket): number | null {
    if (m.volume == null || m.volume <= 0 || !Number.isFinite(m.volume)) return null;
    return m.dominantCapital / m.volume;
}

export function resolveDeploymentTotal(d: Deployment, bankroll: number): number {
    return d.mode === 'fraction'
        ? Math.max(0, d.deployFraction) * bankroll
        : Math.max(0, d.totalCapital);
}

function mean(xs: number[]): number | null {
    return xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null;
}

function median(xs: number[]): number | null {
    if (!xs.length) return null;
    const s = [...xs].sort((a, b) => a - b);
    const mid = Math.floor(s.length / 2);
    return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

// ---------------------------------------------------------------------------
// Weights (proportional engine, plan §3b)
// ---------------------------------------------------------------------------

function rawWeight(m: SimMarket, signal: WeightSignal, blendBeta: number): number {
    switch (signal) {
        case 'capital':
            return Math.max(m.dominantCapital, 0);
        case 'volume':
            return Math.max(m.volume ?? 0, 0);
        case 'capitalToVolume': {
            const r = capitalToVolume(m);
            return r != null && r > 0 ? r : 0;
        }
        case 'blend': {
            const cap = Math.max(m.dominantCapital, 0);
            const vol = Math.max(m.volume ?? 0, 0);
            const beta = Math.min(Math.max(blendBeta, 0), 1);
            // cap^β * vol^(1-β); 0^0 === 1 in JS, guard the degenerate all-zero case
            return Math.pow(cap, beta) * Math.pow(vol, 1 - beta);
        }
    }
}

/**
 * Normalized weights over `markets`, concentrated by α. Returns one number per
 * input market (same order), summing to 1. Falls back to equal weights when the
 * raw-weight mass is zero.
 */
function normalizedWeights(
    markets: SimMarket[],
    signal: WeightSignal,
    blendBeta: number,
    alpha: number
): number[] {
    const n = markets.length;
    if (n === 0) return [];
    const raw = markets.map(m => {
        const w = Math.max(rawWeight(m, signal, blendBeta), 0);
        return alpha === 0 ? (w > 0 ? 1 : 0) : Math.pow(w, alpha);
    });
    const sum = raw.reduce((a, b) => a + b, 0);
    if (sum <= 0) return raw.map(() => 1 / n); // equal-weight fallback
    return raw.map(r => r / sum);
}

// ---------------------------------------------------------------------------
// Stake assignment per algo (pre-wrappers)
// ---------------------------------------------------------------------------

function baseStakes(markets: SimMarket[], cfg: SimConfig, T: number): number[] {
    const algo = cfg.algo;
    const n = markets.length;
    if (n === 0) return [];

    switch (algo.kind) {
        case 'fixedDollars':
            return markets.map(() => Math.max(algo.dollarsPerMarket, 0));

        case 'fixedShares':
            // shares are converted to dollars at the entry price: stake$ = shares * a
            return markets.map(m => Math.max(algo.sharesPerMarket, 0) * (m.avgEntry ?? 0));

        case 'fixedFraction':
            // equal split of T (≡ proportional with α=0)
            return markets.map(() => T / n);

        case 'volumeFraction':
            return markets.map(m => Math.max(algo.volumeFraction, 0) * Math.max(m.volume ?? 0, 0));

        case 'proportional': {
            const w = normalizedWeights(markets, algo.signal, algo.blendBeta, algo.concentration);
            return w.map(wi => T * wi);
        }
    }
}

/** Whether the algo targets a fixed budget T (so capping can redistribute). */
function isBudgeted(algo: SizingAlgo): boolean {
    return algo.kind === 'fixedFraction' || algo.kind === 'proportional';
}

// ---------------------------------------------------------------------------
// Cap application with optional redistribution
// ---------------------------------------------------------------------------

/**
 * Apply per-market ceilings to `stakes`. `caps[i]` may be Infinity (no ceiling).
 *
 * redistribute=false: stake_i = min(stake_i, cap_i). Book may be under-deployed.
 * redistribute=true : freeze capped markets at their ceiling and re-spread the
 *   freed dollars across still-uncapped markets in proportion to their original
 *   target weights, iterating until stable. Conserves the pre-cap total when the
 *   uncapped markets have enough headroom.
 *
 * Returns new stakes plus a per-market "capped" flag.
 */
function applyCaps(
    stakes: number[],
    caps: number[],
    redistribute: boolean
): { stakes: number[]; capped: boolean[] } {
    const n = stakes.length;
    const out = stakes.slice();
    const capped = new Array<boolean>(n).fill(false);

    if (!redistribute) {
        for (let i = 0; i < n; i++) {
            if (out[i] > caps[i]) {
                out[i] = caps[i];
                capped[i] = true;
            }
        }
        return { stakes: out, capped };
    }

    // Budget to preserve = the total we originally wanted to deploy.
    const target = stakes.reduce((a, b) => a + b, 0);
    const frozen = new Array<boolean>(n).fill(false);
    // Weights for re-spreading = original target stakes (so relative intent holds).
    const baseWeights = stakes.slice();

    // Iterate: each pass, anything exceeding its cap freezes at the cap; the
    // remaining budget is re-spread over unfrozen markets by base weight.
    for (let pass = 0; pass < n + 1; pass++) {
        const frozenSum = out.reduce((a, s, i) => (frozen[i] ? a + s : a), 0);
        const remaining = Math.max(target - frozenSum, 0);
        const wSum = baseWeights.reduce((a, w, i) => (frozen[i] ? a : a + w), 0);

        if (wSum <= 0) break;
        for (let i = 0; i < n; i++) {
            if (!frozen[i]) out[i] = remaining * (baseWeights[i] / wSum);
        }

        // Freeze any newly-over-cap markets.
        let changed = false;
        for (let i = 0; i < n; i++) {
            if (!frozen[i] && out[i] > caps[i] + 1e-9) {
                out[i] = caps[i];
                frozen[i] = true;
                capped[i] = true;
                changed = true;
            }
        }
        if (!changed) break;
    }

    return { stakes: out, capped };
}

// ---------------------------------------------------------------------------
// Main entry point
// ---------------------------------------------------------------------------

export function runSimulation(markets: SimMarket[], cfg: SimConfig): SimResult {
    const T = resolveDeploymentTotal(cfg.deployment, cfg.bankroll);

    const allSim = markets.filter(isSimulatable);
    const skipped = markets.filter(m => !isSimulatable(m));

    // NEW: local threshold filter
    let filteredByThreshold: SimMarket[] = [];
    let thresholdPassed = allSim;
    if (cfg.minCapitalThreshold != null || cfg.minVolumeThreshold != null) {
        thresholdPassed = allSim.filter(m => {
            if (cfg.minCapitalThreshold != null && m.dominantCapital < cfg.minCapitalThreshold) {
                filteredByThreshold.push(m);
                return false;
            }
            if (cfg.minVolumeThreshold != null && (m.volume ?? 0) < cfg.minVolumeThreshold) {
                filteredByThreshold.push(m);
                return false;
            }
            return true;
        });
    }
    // Optional selection rule: at most one bet per event. Keep the first
    // simulatable market per event (input order == API ranking), exclude the rest.
    // Markets with no eventId are always traded (treated as their own event).
    let sim = thresholdPassed; // (was: let sim = allSim)
    let nExcludedByEvent = 0;
    if (cfg.oneBetPerEvent) {
        const seen = new Set<string>();
        const kept: SimMarket[] = [];
        for (const m of allSim) {
            if (m.eventId == null) {
                kept.push(m);
                continue;
            }
            if (seen.has(m.eventId)) {
                nExcludedByEvent += 1;
                continue;
            }
            seen.add(m.eventId);
            kept.push(m);
        }
        sim = kept;
    }

    // 1) Base stakes from the algo.
    let stakes = baseStakes(sim, cfg, T);
    const cappedFlags = new Array<boolean>(sim.length).fill(false);
    const redistribute = cfg.wrappers.redistributeCapped && isBudgeted(cfg.algo);

    // 2) Wrapper: liquidity cap (stake_i <= k * volume_i).
    if (cfg.wrappers.liquidityCapFrac != null) {
        const k = cfg.wrappers.liquidityCapFrac;
        const caps = sim.map(m =>
            m.volume != null && m.volume > 0 ? k * m.volume : Infinity
        );
        const res = applyCaps(stakes, caps, redistribute);
        stakes = res.stakes;
        res.capped.forEach((c, i) => (cappedFlags[i] = cappedFlags[i] || c));
    }

    // 3) Wrapper: per-event cap (Σ stake over an event <= f * bankroll).
    if (cfg.wrappers.maxStakePerEventFrac != null) {
        const eventCap = cfg.wrappers.maxStakePerEventFrac * cfg.bankroll;
        // Group indices by event (null eventId => its own singleton "event").
        const byEvent = new Map<string, number[]>();
        sim.forEach((m, i) => {
            const key = m.eventId ?? `__solo__${i}`;
            const arr = byEvent.get(key);
            if (arr) arr.push(i);
            else byEvent.set(key, [i]);
        });
        for (const idxs of byEvent.values()) {
            const total = idxs.reduce((a, i) => a + stakes[i], 0);
            if (total > eventCap && total > 0) {
                const scale = eventCap / total;
                for (const i of idxs) {
                    stakes[i] *= scale;
                    cappedFlags[i] = true;
                }
            }
        }
        // Note: the per-event cap deliberately does NOT redistribute across events —
        // freed capital staying un-deployed is the correlation constraint working.
    }

    // 4) PnL + fees per bet.
    const bets: BetResult[] = [];
    const rets: number[] = [];
    let totalStaked = 0;
    let totalGross = 0;
    let totalFees = 0;
    let nBets = 0;
    let nCapped = 0;

    for (let i = 0; i < sim.length; i++) {
        const m = sim[i];
        const stake = stakes[i] > 0 ? stakes[i] : 0;
        const a = m.avgEntry as number; // simulatable => non-null, > 0
        const mk = m.markPrice as number;
        const shares = stake / a;
        const gross = stake * (mk / a - 1);
        const fee = stake > 0 ? cfg.fees.perTrade + stake * (cfg.fees.bps / 10000) : 0;
        const net = gross - fee;
        const ret = stake > 0 ? net / stake : 0;

        if (stake > 0) {
            totalStaked += stake;
            totalGross += gross;
            totalFees += fee;
            nBets += 1;
            rets.push(ret);
        }
        if (cappedFlags[i]) nCapped += 1;

        bets.push({
            marketId: m.marketId,
            eventId: m.eventId,
            side: m.dominantSide,
            entryPrice: a,
            markPrice: mk,
            capitalToVolume: capitalToVolume(m),
            stakeDollars: stake,
            stakeShares: shares,
            grossPnl: gross,
            fee,
            pnl: net,
            ret,
            capped: cappedFlags[i],
            skipped: false,
            filteredByThreshold: false, // NEW
        });
    }

    // Skipped rows surface in the list but never stake.
    for (const m of skipped) {
        bets.push({
            marketId: m.marketId,
            eventId: m.eventId,
            side: m.dominantSide,
            entryPrice: m.avgEntry,
            markPrice: m.markPrice,
            capitalToVolume: capitalToVolume(m),
            stakeDollars: 0,
            stakeShares: 0,
            grossPnl: 0,
            fee: 0,
            pnl: 0,
            ret: 0,
            capped: false,
            skipped: true,
            filteredByThreshold: false, // NEW
        });
    }

    for (const m of filteredByThreshold) {
        bets.push({
            marketId: m.marketId,
            eventId: m.eventId,
            side: m.dominantSide,
            entryPrice: m.avgEntry,
            markPrice: m.markPrice,
            capitalToVolume: capitalToVolume(m),
            stakeDollars: 0,
            stakeShares: 0,
            grossPnl: 0,
            fee: 0,
            pnl: 0,
            ret: 0,
            capped: false,
            skipped: false,
            filteredByThreshold: true,
        });
    }

    const totalPnl = totalGross - totalFees;
    const wins = rets.filter(r => r > 0).length;

    const summary: SimSummary = {
        bankroll: cfg.bankroll,
        deploymentTotal: isBudgeted(cfg.algo) ? T : 0,
        totalStaked,
        totalGrossPnl: totalGross,
        totalFees,
        totalPnl,
        roi: totalStaked > 0 ? totalPnl / totalStaked : null,
        hitRate: nBets > 0 ? wins / nBets : null,
        meanRet: mean(rets),
        medianRet: median(rets),
        worstRet: rets.length ? Math.min(...rets) : null,
        nBets,
        nCapped,
        nSkipped: skipped.length,
        nExcludedByEvent,
        utilization: cfg.bankroll > 0 ? totalStaked / cfg.bankroll : 0,
        nFilteredByThreshold: filteredByThreshold.length,
    };

    return { bets, summary };
}

// ---------------------------------------------------------------------------
// Sensible defaults the UI can start from.
// ---------------------------------------------------------------------------

export const DEFAULT_SIM_CONFIG: SimConfig = {
    bankroll: 10_000,
    deployment: { mode: 'fraction', deployFraction: 1 },
    algo: {
        kind: 'proportional',
        signal: 'capital',
        blendBeta: 0.5,
        concentration: 1,
    },
    wrappers: {
        liquidityCapFrac: null,
        maxStakePerEventFrac: null,
        redistributeCapped: false, // resolved decision: off by default
    },
    fees: { perTrade: 0, bps: 0 }, // resolved decision: 0 by default
    oneBetPerEvent: false, // trade all markets by default; toggle to one per event
    minVolumeThreshold: 0,
    minCapitalThreshold: 0,
};